import React, { CSSProperties } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import C3 from "c3";
import { ChartRowDataArray } from "../../../../../webapi/constants";
import {
  getProfile,
  IProfileRow,
} from "../../../../../webapi/service/getProfile";
import { getProfiles } from "../../../../../webapi/service/getProfiles";
import { updateProfile } from "../../../../../webapi/service/updateProfile";
import EditChartDialog from "./EditChartDialog";
import { IConfirmDialogParams } from "../../../../../common/ConfirmDialog";
import AuthService from "../../../../../common/AuthService";
import BackButton from "../../../common/BackButton";
import {
  drawChart,
  getChartTableInfo,
  ChartSize,
  createChartTableStyle,
} from "../common/chart";
import { ChartList, ChartDialogConfig } from "../../../../../common/constants";
import "../../../../main.css";
import "../../projectdetail.css";

function drawCharts(
  profiles: IProfileRow[],
  chartid_fmt: string,
  width: number,
  height: number
) {
  console.time("drawCharts");
  const charts = profiles.map((profile, i) => {
    const { channel, data, datanum } = profile;
    return drawChart(
      channel,
      chartid_fmt + String(i),
      width,
      height,
      data,
      datanum
    );
  });
  console.timeEnd("drawCharts");
  return charts;
}

export interface IProps extends WithTranslation {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * メインスペースの幅
   */
  mainSpaceWidth: number;
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * 戻るボタンが押された場合に呼び出されます
   */
  onBack: () => void;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * 確認ダイアログを表示します。
   * @param params - 確認ダイアログパラメータ
   */
  showConfirm: (params: IConfirmDialogParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
  /**
   * 非操作タイムアウトをリセットする場合に呼び出します
   */
  resetUnoperatedTimeout: () => void;
}

interface IState {
  /**
   * プロファイル情報一覧
   */
  profiles: IProfileRow[];
  /**
   * プロファイル編集ダイアログを表示する場合はtrue
   */
  showDialog: boolean;
  /**
   * 編集ダイアログ表示対象プロファイル
   */
  selectProfile: IProfileRow | undefined;
  /**
   * プロファイル編集ダイアログチャート幅
   */
  width: number;
  /**
   * プロファイル編集ダイアログチャート高さ
   */
  height: number;
}

class ResultProfilePanel extends React.Component<IProps, IState> {
  private readonly chartid_fmt = "chart_result_prof_";
  private readonly DefaultChartSize: ChartSize = {
    width: ChartList.RESULT_PROFILE_WIDTH,
    height: ChartList.RESULT_PROFILE_HEIGHT,
  };
  private readonly dialog_area_correction =
    ChartDialogConfig.CHAERT_DIALOG_AREA_CORRECTION;
  private _charts: C3.ChartAPI[] | null = null;
  private _chartSize: ChartSize = this.DefaultChartSize;
  private readonly ChartListThreshold = ChartList.CHART_LIST_THRESHOLD;

  constructor(props: IProps) {
    super(props);

    this.state = {
      profiles: [],
      showDialog: false,
      selectProfile: undefined,
      width: window.innerWidth * this.dialog_area_correction,
      height: window.innerHeight * this.dialog_area_correction,
    };

    this.handleOpenEditDialog = this.handleOpenEditDialog.bind(this);
    this.handleCloseEditDialog = this.handleCloseEditDialog.bind(this);
    this.handleUpdateChart = this.handleUpdateChart.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.chartSize = this.chartSize.bind(this);
  }

  chartSize(size?: ChartSize) {
    if (size) {
      this._chartSize = size;
    }
    return this._chartSize;
  }

  createCharts(prots: IProfileRow[]): void {
    const { chartWidth, chartHeight } = getChartTableInfo(
      this.props.mainSpaceWidth,
      this.DefaultChartSize,
      true
    );
    this._charts = drawCharts(prots, this.chartid_fmt, chartWidth, chartHeight);
    this.chartSize({ width: chartWidth, height: chartHeight });
  }

  destroyCharts(): void {
    if (this._charts) {
      this._charts.forEach((chart) => {
        chart.destroy();
      });
      this._charts = null;
    }
  }

  componentDidMount() {
    this.handleLoadProfile(true);
    window.addEventListener("resize", this.handleResize);
  }

  componentDidUpdate(prevProps: IProps) {
    if (this.props.projectid !== prevProps.projectid) {
      this.setState({ profiles: [] });
      this.destroyCharts();
      this.handleLoadProfile(true);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  handleResize() {
    const { innerWidth, innerHeight } = window;
    this.setState({
      width: innerWidth * this.dialog_area_correction,
      height: innerHeight * this.dialog_area_correction,
    });
  }

  handleLoadProfile(loading: boolean) {
    const { authService, projectid, onLoading, onCommunicationError } =
      this.props;
    onLoading(loading);
    getProfiles({
      authService: authService,
      projectid: projectid,
      threshold: this.ChartListThreshold,
      success: (profiles) => {
        this.setState({ profiles: profiles }, () => {
          this.createCharts(profiles);
          this.handleResize();
          onLoading(false);
        });
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleOpenEditDialog(channel: string) {
    const { authService, projectid, onLoading, onCommunicationError } =
      this.props;
    onLoading(true);
    getProfile({
      authService: authService,
      projectid: projectid,
      channel: channel,
      isRetry401: true,
      success: (profile) => {
        this.setState({ showDialog: true, selectProfile: profile });
        onLoading(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleCloseEditDialog() {
    this.setState({ showDialog: false, selectProfile: undefined });
  }

  handleUpdateChart(datas: ChartRowDataArray) {
    const {
      authService,
      projectid,
      onLoading,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = this.props;
    if (this.state.selectProfile) {
      const { channel, etag } = this.state.selectProfile;
      onLoading(true);
      resetUnoperatedTimeout();
      updateProfile({
        authService: authService,
        projectid: projectid,
        channel: channel,
        datas: datas,
        etag: etag,
        success: () => {
          this.handleCloseEditDialog();
          this.handleLoadProfile(false);
        },
        fail: (id, status) => {
          onCommunicationError(id, status);
        },
      });
    }
  }

  createChartTable(profiles: IProfileRow[], chartid_fmt: string) {
    const charts: JSX.Element[] = [];
    profiles.forEach((profile, i) => {
      charts.push(
        <div className="chart-list-item" key={i}>
          <div
            id={chartid_fmt + String(i)}
            onClick={() => this.handleOpenEditDialog(profile.channel)}
          ></div>
        </div>
      );
    });
    return charts;
  }

  render() {
    const { mainSpaceWidth, onBack, showConfirm, t } = this.props;
    const { profiles, showDialog, selectProfile, width, height } = this.state;
    const chartTableStyle = createChartTableStyle(
      mainSpaceWidth,
      this.DefaultChartSize,
      this._charts,
      this.chartSize
    );
    const charts = this.createChartTable(profiles, this.chartid_fmt);
    const dialog: CSSProperties | undefined = showDialog
      ? undefined
      : { display: "none" };
    return (
      <div className="mainspace">
        <div className="mainspace-title">
          <h3>{t("profile_results")}</h3>
        </div>
        <div className="mainspace-toolbar">
          <BackButton onClick={onBack} />
        </div>
        <div className="chart-list-area">
          <div style={chartTableStyle}>{charts}</div>
        </div>
        <div className="overlay" style={dialog}>
          <EditChartDialog
            show={showDialog}
            width={width}
            height={height}
            profile={selectProfile}
            onOk={this.handleUpdateChart}
            onCancel={this.handleCloseEditDialog}
            showConfirm={showConfirm}
          />
        </div>
      </div>
    );
  }
}

/**
 * プロファイル作成結果画面
 */
export default withTranslation()(ResultProfilePanel);
